import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { TemplateStack, TemplateSection } from '../template-shell';
import { DomainMetadataFields } from './domain-metadata-fields';
import { VnnicStepsForm } from './vnnic-steps-form';
import type { ProductTemplateProps } from '../template-props';
import type { ContractFormValues } from '../../contract-form';

/**
 * Domain template — `Domain.TLDs` (international TLD registration) and
 * `Domain.VNNIC` (Vietnamese registrar). Both write into
 * `products.{index}.service_domain_metadata`; service_configoptions stays empty
 * (domain pricing is handled outside config options in legacy).
 *
 * TLDs: metadata fields only (domain_type, domain_value, period, eppcode).
 * VNNIC: metadata fields + addons + dns_option + 4-step VNNIC contact form.
 */
export function DomainTemplate({ index, templateName }: ProductTemplateProps) {
  const { getValues, setValue } = useFormContext<ContractFormValues>();

  const metaPath = `products.${index}.service_domain_metadata` as const;
  const isVNNIC = templateName === 'Domain.VNNIC';

  // Seed VNNIC defaults once — guard against clobbering user edits.
  // TLDs has no required seeds; the metadata object starts empty and gets
  // populated as the user fills each field.
  useEffect(() => {
    if (!isVNNIC) return;
    const current = getValues(metaPath as never) as unknown as Record<string, unknown> | undefined;
    // Already seeded when domain_extra_fields exists.
    if (current && typeof current === 'object' && 'domain_extra_fields' in current) return;
    setValue(
      metaPath as never,
      {
        domain_extra_fields: { for: 'canhan' },
        dns_option: 'default',
        domain_type: 'register',
        sendmail: true,
      } as never,
      { shouldDirty: true },
    );
  }, [isVNNIC, metaPath, getValues, setValue]);

  return (
    <TemplateStack>
      <TemplateSection title={isVNNIC ? 'Thông tin tên miền VNNIC' : 'Thông tin tên miền'}>
        <DomainMetadataFields metaPath={metaPath} isVNNIC={isVNNIC} />
      </TemplateSection>

      {/* VNNIC requires full registrant contact info across 4 steps */}
      {isVNNIC && <VnnicStepsForm metaPath={metaPath} />}
    </TemplateStack>
  );
}
